import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface PendingActions {
  /** Whether a gesture on this action is in flight, from either pane. */
  isPending: (actionId: string) => boolean;
  /** Marks the action in flight until the gesture settles, and hands its outcome back. */
  track: (actionId: string, gesture: Promise<void>) => Promise<void>;
}

/**
 * Outside the tab — a pane rendered on its own in a story or a test — there is only one card per
 * action, and that card's own `isDone` already covers it.
 */
const PendingActionsContext = createContext<PendingActions>({
  isPending: () => false,
  track: (_actionId, gesture) => gesture,
});

/**
 * The actions a gesture is in flight on, shared across both panes. Each pane runs its own
 * `useActionCard`, whose optimistic `isDone` disables only the card that was clicked, so without
 * this the other pane's card would still offer Execute and Reject on the same action.
 */
export const PendingActionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());

  const track = useCallback(async (actionId: string, gesture: Promise<void>): Promise<void> => {
    setPendingIds((ids) => new Set(ids).add(actionId));
    try {
      await gesture;
    } finally {
      setPendingIds((ids) => {
        const next = new Set(ids);
        next.delete(actionId);

        return next;
      });
    }
  }, []);

  const value = useMemo(
    () => ({ isPending: (actionId: string) => pendingIds.has(actionId), track }),
    [pendingIds, track]
  );

  return <PendingActionsContext.Provider value={value}>{children}</PendingActionsContext.Provider>;
};

export const usePendingActions = (): PendingActions => useContext(PendingActionsContext);
